import { useState, useRef, useEffect } from 'react';

export function useSwipeGesture(
    threshold: number = 50,
    onLeftSwipe?: () => void,
    onRightSwipe?: () => void,
    isDisabled: boolean = false
) {
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);
    const touchStartYRef = useRef<number>(0);
    const elementRef = useRef<HTMLElement | null>(null);
    const isSwipingRef = useRef<boolean>(false);

    useEffect(() => {
        const container = elementRef.current;
        if (!container) return;

        const preventScroll = (e: TouchEvent) => {
            if (isSwipingRef.current) {
                e.preventDefault();
            }
        };

        container.addEventListener('touchmove', preventScroll, { passive: false });

        return () => {
            container.removeEventListener('touchmove', preventScroll);
        };
    }, []);

    const handleTouchStart = (e: React.TouchEvent) => {
        if (isDisabled) return;

        elementRef.current = e.currentTarget as HTMLElement;
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
        touchStartYRef.current = e.targetTouches[0].clientY;
        isSwipingRef.current = false;
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (isDisabled || touchStart === null) return;

        const currentX = e.targetTouches[0].clientX;
        const currentY = e.targetTouches[0].clientY;
        const diffX = currentX - touchStart;
        const diffY = currentY - touchStartYRef.current;

        // Solo considerar deslizamientos horizontales
        if (Math.abs(diffX) > Math.abs(diffY) * 2 && Math.abs(diffX) > 10) {
            setTouchEnd(currentX);
            isSwipingRef.current = true;

            // Feedback visual durante el deslizamiento
            if (elementRef.current) {
                const maxTransform = 30;
                const transformX = Math.min(Math.abs(diffX) / 3, maxTransform) * Math.sign(diffX);
                elementRef.current.style.transform = `translateX(${transformX}px)`;
            }

            // No llamamos a e.preventDefault() aquí, lo hacemos en el listener nativo
        }
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        if (isDisabled) return;

        // Resetear transformación y estado
        if (elementRef.current) {
            elementRef.current.style.transform = '';
        }
        isSwipingRef.current = false;

        if (touchStart === null) return;

        const end = e.changedTouches[0].clientX;
        setTouchEnd(end);

        const distance = touchStart - end;
        const isLeftSwipe = distance > threshold;
        const isRightSwipe = distance < -threshold;

        // Pequeño retraso para que la animación de reset termine
        setTimeout(() => {
            if (isLeftSwipe && onLeftSwipe) {
                onLeftSwipe();
            }

            if (isRightSwipe && onRightSwipe) {
                onRightSwipe();
            }
        }, 10);
    };

    return {
        touchStart,
        touchEnd,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
        isLeftSwipe: touchStart !== null && touchEnd !== null ? (touchStart - touchEnd > threshold) : false,
        isRightSwipe: touchStart !== null && touchEnd !== null ? (touchStart - touchEnd < -threshold) : false
    };
}